import { useEffect, useState } from "react";
import { FilterGroup, FilterOption, FilterSidebar, TextInput } from "@amber/ui";
import { HEALTH_LABELS, HEALTH_ORDER, type SourcesResponse } from "../../contracts/index";
import { listOf, toggled } from "../filterList";
import type { SourcesSearch } from "../search";
import { HealthMark } from "./SourceVisuals";

export function SourcesSidebar({
  data,
  search,
  set,
  open,
}: {
  open: boolean;
  data: SourcesResponse | undefined;
  search: SourcesSearch;
  set: (patch: Partial<SourcesSearch>) => void;
}) {
  const [text, setText] = useState(search.q ?? "");
  const [find, setFind] = useState("");
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const q = text.trim() || undefined;
      if (q !== search.q) set({ q });
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [text, search.q, set]);
  const people = new Map<string, { name: string; n: number }>();
  for (const s of data?.sources ?? [])
    for (const p of s.people) people.set(p.id, { name: p.name, n: (people.get(p.id)?.n ?? 0) + 1 });
  const platforms = (data?.platforms ?? []).filter((p) => p.sources > 0);
  return (
    <FilterSidebar label="Source filters" open={open}>
      <FilterGroup title="Source name or URL">
        <label className="sr-only" htmlFor="sources-filter">
          Filter by name or URL
        </label>
        <TextInput
          id="sources-filter"
          type="search"
          placeholder="Filter by name or URL"
          className="h-7"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
      </FilterGroup>
      <FilterGroup title="Health">
        {HEALTH_ORDER.map((h) => (
          <FilterOption
            key={h}
            type="checkbox"
            name="health"
            label={HEALTH_LABELS[h]}
            count={data?.counts.by_health[h] ?? 0}
            icon={<HealthMark state={h} />}
            checked={listOf(search.health).includes(h)}
            onChange={() => {
              set({ health: toggled(search.health, h) });
            }}
          />
        ))}
      </FilterGroup>
      <FilterGroup title="Person">
        <label className="sr-only" htmlFor="sources-find-person">
          Find a person
        </label>
        <TextInput
          id="sources-find-person"
          type="search"
          placeholder="Find a person"
          className="mb-1 h-7"
          value={find}
          onChange={(e) => {
            setFind(e.target.value);
          }}
        />
        {[...people]
          .filter(
            ([id, p]) =>
              find.trim() === "" || p.name.toLowerCase().includes(find.trim().toLowerCase()) || search.person === id,
          )
          .sort((a, b) => a[1].name.localeCompare(b[1].name))
          .map(([id, p]) => (
            <FilterOption
              key={id}
              type="checkbox"
              name="person"
              label={p.name}
              count={p.n}
              checked={search.person === id}
              onChange={() => {
                set({ person: search.person === id ? undefined : id });
              }}
            />
          ))}
      </FilterGroup>
      <FilterGroup title="Platform">
        {platforms.map((p) => (
          <FilterOption
            key={p.key}
            type="checkbox"
            name="platform"
            label={p.collection_state === "collected" ? p.label : `${p.label} · Not collected yet`}
            count={p.sources}
            checked={search.platform === p.key}
            onChange={() => {
              set({ platform: search.platform === p.key ? undefined : p.key });
            }}
          />
        ))}
      </FilterGroup>
    </FilterSidebar>
  );
}
